import React, { useCallback, useState } from 'react';
import { TransactionCategory } from '@shipstation/financial-services-ui-components';
import { FormattedMessage } from 'react-intl';

import { BannerLayout, BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { Button } from 'ui-components/components/button';
import { Text, TextType } from 'ui-components/components/text';

import { messages } from './messages';
import { useGlobalModalLaunchContext } from '../../contexts/modal-context/global-modal-context';
import { useTrackEvent, PageEvents, PageEventSourceStrings } from '../../pages/settings/payment-and-subscription/hooks/use-track-event';

/**
 * @typedef {object} SurchargeInfoBannerProps
 * @property {import('../types/fsui').SurchargeEligibility['surchargePercentage']} surchargeAmount - The surcharge amount as a decimal (e.g., 1.9 for 1.9%)
 * @property {string} [className] - Optional additional CSS class name
 */

/**
 * Surcharge Info Banner component that displays credit card fee information
 * @param {SurchargeInfoBannerProps} props
 * @returns {JSX.Element}
 */
export const SurchargeInfoBanner = ({ surchargeAmount, className }) => {
  const { openBillingInfoModal } = useGlobalModalLaunchContext();
  const [isOpen, setIsOpen] = useState(true);

  const trackConfirmClick = useTrackEvent(PageEvents.SURCHARGE_CONNECT_BANK_CLICK, PageEventSourceStrings.SURCHARGE_BANNER);

  const onConnectBankAccount = useCallback(() => {
    openBillingInfoModal({ transactionCategory: TransactionCategory.AddFunds, trackConfirmClick });
  }, [openBillingInfoModal, trackConfirmClick]);

  const onClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const percentage = surchargeAmount.toFixed(1);

  const header = <Text textType={TextType.H4} value={<FormattedMessage {...messages.surchargeTitle} values={{ percentage }} />} />;
  const content = <Text textType={TextType.CAPTION} value={messages.surchargeSubtitle} />;

  const buttons = (
    <Button onClick={onConnectBankAccount} theme='tertiary'>
      <FormattedMessage {...messages.connectBankAccount} />
    </Button>
  );

  return (
    isOpen && (
      <BannerMessageV2
        buttons={buttons}
        className={className}
        content={content}
        header={header}
        layout={BannerLayout.Full}
        onClose={onClose}
        status={BannerStatus.Info}
      />
    )
  );
};
